import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useEffect } from 'react';

import { useAppSettingHelper } from '../../hooks/pique/use-app-setting-helper';
import { AppSidebarFallback } from '../app-sidebar';
import type { WorkspaceRootProps } from '../workspace';
import {
  AppContainer as AppContainerWithoutSettings,
  MainContainer,
} from '../workspace';
import { loadingBarStyle } from './app-container.css';
import { enableTranslucentModeAtom } from './setting-modal/atoms';

export const AppContainer = (props: WorkspaceRootProps) => {
  const { appSettings } = useAppSettingHelper();
  const translucentMode = useAtomValue(enableTranslucentModeAtom);

  useEffect(() => {
    document.documentElement.dataset['translucent'] = String(translucentMode);
  }, [translucentMode]);

  return (
    <AppContainerWithoutSettings
      useNoisyBackground={appSettings.enableNoisyBackground}
      useBlurBackground={translucentMode}
      {...props}
    >
      {props.children}
    </AppContainerWithoutSettings>
  );
};

export const AppFallback = (): ReactElement => {
  return (
    <AppContainer>
      <AppSidebarFallback />
      <MainContainer>
        <LoadingFallback />
      </MainContainer>
    </AppContainer>
  );
};

const LoadingFallback = () => {
  return (
    <div
      style={{
        position: 'absolute',
        top: '40%',
        left: '66px',
        transform: 'translateY(-50%)',
        width: '80%',
        maxWidth: '900px'
      }}
    >
      {/* Header placeholder */}
      <div style={{ marginBottom: '3vh' }}>
        <div
          className={loadingBarStyle}
          style={{
            width: '230px',
            height: '2.5vh',
            borderRadius: '4px',
            marginBottom: '1vh',
            opacity: 0.2
          }}
        />
        <div
          className={loadingBarStyle}
          style={{
            width: '180px',
            height: '2vh',
            borderRadius: '4px',
            opacity: 0.15
          }}
        />
      </div>

      {/* Content placeholders */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
        {[65, 60, 55, 50, 45, 50, 55, 60, 35].map((width, i) => (
          <div
            key={i}
            className={loadingBarStyle}
            style={{
              width: `${width}%`,
              height: '2.5vh',
              borderRadius: '8px',
              opacity: Math.max(0.05, 0.25 - i * 0.02)
            }}
          />
        ))}
      </div>
    </div>
  );
};